import type {
  AssistantReportDefinition,
  AssistantReportPreview,
} from "@triedge/domain-core";
import type {
  ReportGenerationAdvancedVisualizationChartColumn,
  ReportGenerationAdvancedVisualizationChartSnapshot,
  ReportGenerationAdvancedVisualizationDatasourcePlan,
  ReportGenerationAdvancedVisualizationEntityLink,
  ReportGenerationAdvancedVisualizationGraphqlOperation,
  ReportGenerationAdvancedVisualizationJoin,
  ReportGenerationAdvancedVisualizationSnapshot,
  ReportGenerationFieldAuthority,
} from "@triedge/ai-assistant-ui/components";
import { resolveReportGenerationVisualizationPolicy } from "@triedge/ai-assistant-ui/components";
import type { ShellPersona } from "../app-shell/moduleRegistry";
import type { CognitoProfile } from "../utils/cognito-profile";
import type {
  ReportGenerationFieldOption,
  ReportGenerationModuleViewModel,
} from "./reportGenerationFiltering";

type PreviewColumnLike = {
  fieldId: string;
  label: string;
  moduleLabel: string;
  entityId?: string | null;
  authority?: Partial<ReportGenerationFieldAuthority> | null;
};

type PreviewRowLike = {
  cells: Record<
    string,
    AssistantReportPreview["rows"][number]["cells"][string] | undefined
  >;
};

type BuildReportGenerationAdvancedVisualizationSnapshotInput = {
  actorSource: string;
  profile: CognitoProfile | null;
  effectivePersona: ShellPersona;
  definition: AssistantReportDefinition;
  modules: ReportGenerationModuleViewModel[];
  inaccessibleEntities: ReportGenerationModuleViewModel["entities"];
  availableFields: ReportGenerationFieldOption[];
  preview?: AssistantReportPreview | null;
  previewColumns: PreviewColumnLike[];
  previewRows: PreviewRowLike[];
  joins: string[];
  totalRowCount: number;
  warnings: string[];
};

const sortedUnique = (values: readonly unknown[]): string[] =>
  [
    ...new Set(
      values.map((value) => String(value ?? "").trim()).filter(Boolean),
    ),
  ].sort();

const maybeString = (value: unknown): string | null => {
  const normalized = String(value ?? "").trim();
  return normalized || null;
};

const toFieldAuthority = (
  authority: Partial<ReportGenerationFieldAuthority> | null | undefined,
): ReportGenerationFieldAuthority | null => {
  if (
    !authority?.operationId ||
    !authority.operationKind ||
    !authority.appSyncRouter ||
    !authority.appSyncField ||
    !authority.resultPath
  ) {
    return null;
  }
  return {
    scope: maybeString(authority.scope) ?? "unknown",
    status: maybeString(authority.status) ?? "unknown",
    source: maybeString(authority.source),
    domainPackage: maybeString(authority.domainPackage),
    domainType: maybeString(authority.domainType),
    domainSchema: maybeString(authority.domainSchema),
    operationId: authority.operationId,
    operationKind: authority.operationKind,
    appSyncRouter: authority.appSyncRouter,
    appSyncField: authority.appSyncField,
    resultPath: authority.resultPath,
    routeFamily: maybeString(authority.routeFamily),
    datasourceFamily: maybeString(authority.datasourceFamily),
    joinKey: maybeString(authority.joinKey),
    permission: maybeString(authority.permission),
  };
};

const buildOperationKey = (
  operation: ReportGenerationAdvancedVisualizationGraphqlOperation,
): string =>
  [
    operation.id,
    operation.kind,
    operation.appSyncRouter,
    operation.appSyncField,
    operation.resultPath,
  ].join("|");

const buildGraphqlOperations = (
  authorities: readonly (
    | Partial<ReportGenerationFieldAuthority>
    | null
    | undefined
  )[],
): ReportGenerationAdvancedVisualizationGraphqlOperation[] => {
  const operations = new Map<
    string,
    ReportGenerationAdvancedVisualizationGraphqlOperation
  >();
  for (const authority of authorities) {
    const normalized = toFieldAuthority(authority);
    if (!normalized) {
      continue;
    }
    const operation = {
      id: normalized.operationId,
      kind: normalized.operationKind,
      appSyncRouter: normalized.appSyncRouter,
      appSyncField: normalized.appSyncField,
      resultPath: normalized.resultPath,
      datasourceFamily: normalized.datasourceFamily,
      domainPackage: normalized.domainPackage,
      domainType: normalized.domainType,
      domainSchema: normalized.domainSchema,
      routeFamily: normalized.routeFamily,
    };
    operations.set(buildOperationKey(operation), operation);
  }
  return [...operations.values()].sort((left, right) =>
    buildOperationKey(left).localeCompare(buildOperationKey(right)),
  );
};

const toDatasourcePlan = (
  plan: AssistantReportPreview["datasourcePlan"],
): ReportGenerationAdvancedVisualizationDatasourcePlan | null => {
  if (!plan) {
    return null;
  }
  return {
    module: maybeString(plan.module),
    routeFamily: maybeString(plan.routeFamily),
    requiredDatasourceFamilies: sortedUnique(plan.requiredDatasourceFamilies),
    selectedDatasourceFamilies: sortedUnique(plan.selectedDatasourceFamilies),
    executionPlan: plan.executionPlan.map((step) => ({
      datasourceFamily: step.datasourceFamily,
      executionAdapter: step.executionAdapter,
    })),
    allowed: plan.allowed,
    denialReason: maybeString(plan.denialReason),
  };
};

const buildJoinEndpoints = (
  joinLabels: readonly string[],
  modules: readonly ReportGenerationModuleViewModel[],
): ReportGenerationAdvancedVisualizationJoin[] => {
  const entityIdByLabel = new Map<string, string>();
  for (const entity of modules.flatMap((module) => module.entities)) {
    entityIdByLabel.set(entity.label.trim().toLowerCase(), entity.id);
  }
  const separator = ` ${String.fromCharCode(0x2194)} `;
  return joinLabels.map((label, index) => {
    const [entityText, joinKey = null] = label.split(" linked by ");
    const [leftLabel, rightLabel] = entityText.split(separator);
    return {
      id: `join-${index + 1}`,
      label,
      leftEntityId:
        entityIdByLabel.get(
          String(leftLabel ?? "")
            .trim()
            .toLowerCase(),
        ) ?? null,
      rightEntityId:
        entityIdByLabel.get(
          String(rightLabel ?? "")
            .trim()
            .toLowerCase(),
        ) ?? null,
      joinKey,
    };
  });
};

const toEntityLinks = (
  joins: readonly ReportGenerationAdvancedVisualizationJoin[],
  modules: readonly ReportGenerationModuleViewModel[],
): ReportGenerationAdvancedVisualizationEntityLink[] => {
  const entityLabelById = new Map<string, string>();
  for (const entity of modules.flatMap((module) => module.entities)) {
    entityLabelById.set(entity.id, entity.label);
  }
  return joins.map((join) => ({
    id: join.id,
    label: join.label,
    leftEntityId: join.leftEntityId ?? null,
    leftEntityLabel: join.leftEntityId
      ? (entityLabelById.get(join.leftEntityId) ?? null)
      : null,
    rightEntityId: join.rightEntityId ?? null,
    rightEntityLabel: join.rightEntityId
      ? (entityLabelById.get(join.rightEntityId) ?? null)
      : null,
    joinKey: maybeString(join.joinKey),
  }));
};

const buildChartVisualization = (input: {
  definition: AssistantReportDefinition;
  columns: readonly PreviewColumnLike[];
  rows: readonly PreviewRowLike[];
  availableFields: readonly ReportGenerationFieldOption[];
  modules: readonly ReportGenerationModuleViewModel[];
  joins: readonly ReportGenerationAdvancedVisualizationJoin[];
  warnings: readonly string[];
}): ReportGenerationAdvancedVisualizationChartSnapshot => {
  const availableFieldById = new Map(
    input.availableFields.map((field) => [field.fieldId, field]),
  );
  const entityLabelById = new Map<string, string>();
  for (const entity of input.modules.flatMap((module) => module.entities)) {
    entityLabelById.set(entity.id, entity.label);
  }
  const chartColumns: ReportGenerationAdvancedVisualizationChartColumn[] =
    input.columns.map((column) => {
      const field = availableFieldById.get(column.fieldId);
      const authority = toFieldAuthority(column.authority ?? field?.authority);
      return {
        fieldId: column.fieldId,
        label: column.label,
        fieldType: field?.fieldType ?? "text",
        entityId: column.entityId ?? field?.entityId ?? null,
        entityLabel:
          (column.entityId ? entityLabelById.get(column.entityId) : null) ??
          field?.entityLabel ??
          null,
        domainType: authority?.domainType ?? null,
        domainSchema: authority?.domainSchema ?? null,
        joinKey: authority?.joinKey ?? null,
        permission: authority?.permission ?? null,
        datasourceFamily: authority?.datasourceFamily ?? null,
        operationId: authority?.operationId ?? null,
        operationKind: authority?.operationKind ?? null,
        appSyncRouter: authority?.appSyncRouter ?? null,
        appSyncField: authority?.appSyncField ?? null,
        resultPath: authority?.resultPath ?? null,
        authorityScope: authority?.scope ?? null,
        authorityStatus: authority?.status ?? null,
        authoritySource: authority?.source ?? null,
      };
    });

  const selectedFieldSignature = chartColumns.map((column) => column.fieldId).join("|");
  const filterSignature = JSON.stringify({
    conditions: input.definition.conditions ?? [],
    dateRange: input.definition.dateRange ?? null,
  });
  return resolveReportGenerationVisualizationPolicy({
    columns: chartColumns,
    entityLinks: toEntityLinks(input.joins, input.modules),
    rows: input.rows,
    warnings: input.warnings,
    templateId: input.definition.templateId,
    selectedFieldSignature,
    filterSignature,
  });
};

export const buildReportGenerationAdvancedVisualizationSnapshot = (
  input: BuildReportGenerationAdvancedVisualizationSnapshotInput,
): ReportGenerationAdvancedVisualizationSnapshot => {
  const selectedFieldIds = new Set(input.definition.selectedFields);
  const selectedEntityIds = new Set(input.definition.selectedEntities);
  const fieldAuthorityById = new Map<
    string,
    ReportGenerationFieldAuthority | null
  >();
  for (const field of input.availableFields) {
    fieldAuthorityById.set(field.fieldId, toFieldAuthority(field.authority));
  }
  for (const column of input.preview?.columns ?? input.previewColumns) {
    fieldAuthorityById.set(
      column.fieldId,
      toFieldAuthority(column.authority) ??
        fieldAuthorityById.get(column.fieldId) ??
        null,
    );
  }

  const modules = input.modules
    .map((module) => ({
      moduleId: module.moduleId,
      moduleLabel: module.moduleLabel,
      routeRoot: module.routeRoot,
      entities: module.entities
        .filter((entity) => selectedEntityIds.has(entity.id))
        .map((entity) => ({
          id: entity.id,
          label: entity.label,
          moduleId: entity.moduleId,
          moduleLabel: entity.moduleLabel,
          routeRoot: entity.routeRoot,
          selected: entity.selected,
          authorityScope: maybeString(entity.authorityScope),
          authorityStatus: maybeString(entity.authorityStatus),
          permission: maybeString(entity.permission),
          permissionResolution: {
            allowed: entity.permissionResolution.granted,
            status: maybeString(entity.permissionResolution.status),
            source: maybeString(entity.permissionResolution.source),
            reason: maybeString(entity.permissionResolution.reason),
          },
          fields: entity.visibleCategories
            .flatMap((category) => category.fields)
            .filter((field) => selectedFieldIds.has(field.id))
            .map((field) => ({
              id: field.id,
              label: field.label,
              type: field.type,
              authority:
                toFieldAuthority(field.authority) ??
                fieldAuthorityById.get(field.id) ??
                null,
            })),
        })),
    }))
    .filter((module) => module.entities.length > 0);

  const authorities = [
    ...input.availableFields
      .filter((field) => selectedFieldIds.has(field.fieldId))
      .map((field) => field.authority),
    ...(input.preview?.columns ?? input.previewColumns).map(
      (column) => column.authority,
    ),
  ];
  const datasourcePlan = toDatasourcePlan(
    input.preview?.datasourcePlan ?? null,
  );
  const datasourceFamilies = sortedUnique([
    ...(datasourcePlan?.requiredDatasourceFamilies ?? []),
    ...(datasourcePlan?.selectedDatasourceFamilies ?? []),
    ...authorities.map(
      (authority) => toFieldAuthority(authority)?.datasourceFamily,
    ),
  ]);
  const executionAdapters = sortedUnique(
    datasourcePlan?.executionPlan.map((step) => step.executionAdapter) ?? [],
  );
  const blockedEntityIds = sortedUnique([
    ...(input.preview?.blockedEntityIds ?? []),
    ...input.inaccessibleEntities.map((entity) => entity.id),
  ]);

  const joins = buildJoinEndpoints(input.joins, input.modules);
  const chartVisualization = buildChartVisualization({
    definition: input.definition,
    columns: input.previewColumns,
    rows: input.previewRows,
    availableFields: input.availableFields,
    modules: input.modules,
    joins,
    warnings: input.warnings,
  });

  return {
    pathname: maybeString(input.definition.pathname) ?? "/school/run-reports",
    module: maybeString(input.definition.module) ?? "tuition",
    routeFamily: maybeString(input.definition.routeFamily) ?? "tuition",
    title: maybeString(input.definition.title) ?? "Tuition report",
    actor: {
      persona: input.effectivePersona,
      groups: sortedUnique(input.profile?.groups ?? []),
      permissions: sortedUnique(input.profile?.permissions ?? []),
      source: input.actorSource,
    },
    datasourcePlan,
    datasourceFamilies,
    executionAdapters,
    graphqlOperations: buildGraphqlOperations(authorities),
    modules,
    joins,
    visibleColumnCount: input.previewColumns.length,
    selectedEntityCount: input.definition.selectedEntities.length,
    selectedFieldCount: input.definition.selectedFields.length,
    totalRowCount: input.totalRowCount,
    blockedEntityIds,
    warnings: sortedUnique(input.warnings),
    chartVisualization,
  };
};
